import React, { useState, useEffect } from "react";
import {
  Box,
  Modal,
  Typography,
  IconButton,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Autocomplete,
  TextField,
} from "@mui/material";
import ImgPath from "Constants/Imgs";
import ControlledForm from "Components/ControlledForm";
import { setAlertData } from "../../Redux/Screen/Action";
import { useDispatch } from "react-redux";
import StatusApi from "Service/Status.api";
import { StampListDetailsType } from "DataTypes/Case.type";
import { CheckNumber, CheckStringEmpty, MAX_FILE_SIZE_BYTES, MAX_FILE_SIZE_MB } from "Utils/Common";
import NoData from "Components/NoData";

interface StatusOption {
  pkProcessingStatus: number;
  statusName: string;
  description: string;
  indexProcessingStatus: number;
  numberOfDaysRequired: number;
}

interface StatusUpdateModalProps {
  open: boolean;
  onClose: () => void;
  caseStatus: StatusOption[];
  onStatusUpdate: (selectedStatus: StatusOption) => void;
  currentCaseId?: string | number | null;
}

const StatusUpdateModal: React.FC<StatusUpdateModalProps> = ({
  open,
  onClose,
  caseStatus,
  onStatusUpdate,
  currentCaseId,
}) => {
  const dispatch = useDispatch();
  const [selectedStatusId, setSelectedStatusId] = useState<number | "">("");
  const [highestStatus, setHighestStatus] = useState<StatusOption | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedStamp, setSelectedStamp] =
    useState<StampListDetailsType | null>(null);
  const [stampListDetails, setStampListDetails] = useState<
    StampListDetailsType[]
  >([]);
  // Auto-select the status with the highest indexProcessingStatus on mount
  useEffect(() => {
    if (caseStatus && caseStatus.length > 0) {
      fetchStampDetils();
      const maxIndexStatus = caseStatus.reduce((prev, current) =>
        prev.indexProcessingStatus > current.indexProcessingStatus
          ? prev
          : current
      );
      setSelectedStatusId(maxIndexStatus.pkProcessingStatus);
      setHighestStatus(maxIndexStatus);
    }
  }, [caseStatus]);

  const handleStatusChange = (event: SelectChangeEvent<number>) => {
    setSelectedStatusId(event.target.value as number);
  };

  const handleStampChange = (value: StampListDetailsType | null) => {
    setSelectedStamp(value);
  };

  const fetchStampDetils = async () => {
    const res = await StatusApi.getStampDetails();
    if (res?.success) {
      const formattedData: StampListDetailsType[] = res?.data?.map(
        (item: any) => ({
          pkEStampDetails: CheckNumber(item?.pkEStampDetails),
          stampName: CheckStringEmpty(item?.stampName),
          stampCode: CheckStringEmpty(item?.stampCode),
          stateCode: CheckStringEmpty(item?.stateCode),
          denomination: CheckNumber(item?.denomination),
          stateName: CheckStringEmpty(item?.stateName),
          bActive: item?.bActive,
          dtCreated: CheckStringEmpty(item?.dtCreated),
        })
      );
      setStampListDetails(formattedData);
    }
  };

  const handleUpdateStatus = async () => {
    try {
      if (selectedFile && selectedStamp) {

      if (selectedFile.size > MAX_FILE_SIZE_BYTES) {
        dispatch(setAlertData({
          alertMessage: `The file size exceeds the ${MAX_FILE_SIZE_MB}MB limit.`,
          alertVariant: "error",
          openAlert: true,
        }));
        return;
      }

        const formData = new FormData();
        formData.append("CaseId", String(currentCaseId));
        formData.append("PdfFile", selectedFile);
        formData.append("FileName", selectedFile.name);
        formData.append("EstampCode", selectedStamp?.stampCode || "");
        formData.append("StateCode", selectedStamp?.stateCode || "");

        const res = await StatusApi.eSignDocument(formData);
        if (res?.success) {
          setSelectedFile(null);
          setSelectedStamp(null);
          dispatch(setAlertData(res?.alert));
        }
      }

      const selectedStatus = caseStatus.find(
        (status) => status.pkProcessingStatus === selectedStatusId
      );
      if (selectedStatus) {
        onStatusUpdate(selectedStatus);
        onClose();
      }
      const payload = {
        caseId: currentCaseId,
        statusId: selectedStatusId,
      };
      const res = await StatusApi.updateCaseStatus(payload);
    } catch (e) {
      console.log(e);
    }
  };

  const selectedStatusDetails = caseStatus.find(
    (status) => status.pkProcessingStatus === selectedStatusId
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="status-update-modal-title"
      aria-describedby="status-update-modal-description"
    >
      <Box sx={modalStyle}>
        {/* Header */}
        <Box sx={headerStyle}>
          <Typography variant="h6" sx={{ color: "white", fontWeight: 600 }}>
            Status Update
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <ImgPath.CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
            Update case status for Case ID: {currentCaseId || "N/A"}
          </Typography>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <InputLabel id="status-select-label">Select Status</InputLabel>
            <Select
              labelId="status-select-label"
              id="status-select"
              value={selectedStatusId}
              label="Select Status"
              onChange={handleStatusChange}
              required={true}
            >
              {caseStatus.map((status) => (
                <MenuItem
                  key={status.pkProcessingStatus}
                  value={status.pkProcessingStatus}
                >
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {status.statusName}
                    </Typography>
                    <Typography variant="caption" color="text.primary">
                      {status.numberOfDaysRequired} days required
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Autocomplete
            fullWidth
            id="stamp-autocomplete"
            options={stampListDetails}
            value={selectedStamp}
            onChange={(_, newValue) => handleStampChange(newValue)}
            getOptionLabel={(option) => `${option?.stampName}`}
            filterOptions={(options, { inputValue }) =>
              options?.filter(
                (option) =>
                  option?.stampName
                    ?.toLowerCase()
                    ?.includes(inputValue?.toLowerCase()) ||
                  option?.stateName
                    ?.toLowerCase()
                    ?.includes(inputValue?.toLowerCase()) ||
                  option?.denomination?.toString()?.includes(inputValue)
              )
            }
            renderOption={(props, option) => (
              <Box
                component="li"
                {...props}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start !important", 
                  justifyContent: "flex-start", 
                  textAlign: "left", 
                  width: "100%",
                  py: 0.5,
                }}
              >
                <Typography variant="body1" fontWeight={500}>
                  {option?.stampName}
                </Typography>
                <Typography variant="caption" color="text.primary">
                  State: {option?.stateName}
                </Typography>
                <Typography variant="caption" color="text.primary">
                  Denomination: {option?.denomination}
                </Typography>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                required={true}
                {...params}
                label="Select Stamp"
                variant="outlined"
              />
            )}
            noOptionsText={<NoData />}
          />
          {selectedStatusDetails && (
            <Box
              sx={{
                p: 2,
                backgroundColor: "grey.50",
                borderRadius: 1,
                mb: 3,
              }}
            >
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Status Details:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Description:</strong>{" "}
                {selectedStatusDetails.description}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>Processing Index:</strong>{" "}
                {selectedStatusDetails.indexProcessingStatus}
              </Typography>
              <Typography variant="body2">
                <strong>Days Required:</strong>{" "}
                {selectedStatusDetails.numberOfDaysRequired} days
              </Typography>
              {selectedStatusDetails?.indexProcessingStatus ===
                highestStatus?.indexProcessingStatus && (
                <ControlledForm.Input
                  id="fileSelection"
                  type="file"
                  onChange={(value) =>
                    setSelectedFile(typeof value == "object" ? value[0] : null)
                  }
                  acceptFileTypes=".pdf"
                  label="Upload Document"
                  focused={true}
                />
              )}
            </Box>
          )}
        </Box>

        {/* Footer */}
        <Box sx={footerStyle}>
          <Button
            variant="outlined"
            onClick={onClose}
            sx={{
              mr: 2,
              borderColor: "primary.main",
              color: "primary.main",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateStatus}
            disabled={!selectedStatusId}
          >
            Update Status
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "90%", sm: "500px" },
  maxWidth: "600px",
  backgroundColor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  outline: "none",
  maxHeight: "90vh",
  overflow: "auto",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  backgroundColor: "primary.main",
  px: 3,
  py: 2,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  px: 3,
  py: 2,
  borderTop: "1px solid",
  borderColor: "divider",
};

export default StatusUpdateModal;
